/**
 * What a failed API request actually said.
 *
 * axios's own message is "Request failed with status code 400" — useless in an
 * analytics event and worse in the UI. Studio (NestJS) puts the reason in the
 * body as `{ message, code?, statusCode }`; class-validator failures send
 * `message` as an array.
 */
import { isAxiosError } from 'axios';

export interface RequestErrorDetails {
  /** The server's own message, when it sent one. */
  serverMessage?: string;
  /** Machine-readable reason code from the body, e.g. PLAN_REQUIRED. */
  code?: string;
  /** HTTP status, absent when no response came back. */
  status?: number;
  /** The client-side error message (axios / fetch wording). */
  error: string;
}

export function describeRequestError(err: unknown): RequestErrorDetails {
  const error = err instanceof Error ? err.message : String(err ?? 'Unknown error');

  if (!isAxiosError(err) || !err.response) {
    return { error };
  }

  const data = err.response.data as { message?: unknown; code?: unknown } | undefined;
  const raw = data && typeof data === 'object' ? data.message : undefined;
  const serverMessage = Array.isArray(raw)
    ? raw.filter((m) => typeof m === 'string').join('; ') || undefined
    : typeof raw === 'string' && raw.trim()
      ? raw
      : undefined;
  const code = data && typeof data === 'object' && typeof data.code === 'string' ? data.code : undefined;

  return { serverMessage, code, status: err.response.status, error };
}
